import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Sidebar from '../../components/layout/Sidebar';
import PageContainer from '../../components/layout/PageContainer';
import { useAuth } from '../../hooks/useAuth';
import Overview from './Overview';
import TicketsView from './TicketsView';
import SettingsView from './SettingsView';
import EscalationMatrixView from './EscalationMatrixView';
import { SiteProvider, useSite } from '../../contexts/SiteContext';
import SiteSelector from '../../components/common/SiteSelector';

const CustomerDashboardContent = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { sites, selectedSite, setSelectedSite, loadingSites } = useSite();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const pages = [
    { id: 'dashboard', name: 'Dashboard', icon: 'dashboard' },
    { id: 'tickets', name: 'My Tickets', icon: 'tickets' },
    { id: 'escalation', name: 'Escalation Matrix', icon: 'support' },
    { id: 'settings', name: 'Settings', icon: 'settings' }
  ];

  const containerStyle = {
    display: 'flex',
    minHeight: '100vh',
    background: 'var(--background)'
  };

  const pageSegment = location.pathname.split('/')[2] || 'dashboard';
  const currentPage = pages.some((page) => page.id === pageSegment) ? pageSegment : 'dashboard';

  const setCurrentPage = (pageId) => {
    navigate(`/customer/${pageId}`);
  };

  const navigateToTickets = (statusFilter = '') => {
    if (statusFilter === 'CLOSED') {
      navigate(`/customer/tickets?status=CLOSED`);
    } else if (statusFilter) {
      navigate(`/customer/tickets?status=${statusFilter}`);
    } else {
      navigate('/customer/tickets');
    }
  };

  const mainContentStyle = {
    flex: 1,
    marginLeft: sidebarOpen ? '260px' : '80px',
    transition: 'margin-left 0.3s ease',
    minHeight: '100vh'
  };

  const renderPage = () => {
    switch (currentPage) {
      case 'dashboard':
        return <Overview navigateToTickets={navigateToTickets}/>;
      case 'tickets':
        return <TicketsView />;
      case 'escalation':
        return <EscalationMatrixView />;
      case 'settings':
        return <SettingsView />;
      default:
        return <Overview />;
    }
  };

  const getPageTitle = () => {
    const page = pages.find(p => p.id === currentPage);
    return page?.name || 'Dashboard';
  };

  const getPageSubtitle = () => {
    const subtitles = {
      dashboard: 'Overview of your site and service status',
      tickets: 'View and manage your service tickets',
      escalation: 'Escalation hierarchy for unresolved issues',
      settings: 'Manage your account settings'
    };
    return subtitles[currentPage] || '';
  };

  return (
    <div style={containerStyle}>
      <Sidebar
        user={user}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        pages={pages}
        onLogout={logout}
        isOpen={sidebarOpen}
        setIsOpen={setSidebarOpen}
      />
      <div style={mainContentStyle}>
        <PageContainer
          title={getPageTitle()}
          subtitle={getPageSubtitle()}
        >
          {/* Globally visible Site Selector for all Customer views */}
          {sites.length!==1 && <SiteSelector
            sites={sites}
            selectedSite={selectedSite}
            onChange={setSelectedSite}
            loading={loadingSites}
          />}
          {renderPage()}
        </PageContainer>
      </div>
    </div>
  );
};

const CustomerDashboard = () => {
  return (
    <SiteProvider>
      <CustomerDashboardContent />
    </SiteProvider>
  );
};

export default CustomerDashboard;